import { useEffect, useId, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppAdminEntry } from '@/admin/useAdminApps';
import {
  checkIconFile,
  ICON_ACCEPT,
  useDeleteAppIcon,
  useUploadAppIcon,
} from '@/admin/useAppIcon';
import type { ApiError } from '@/api/client';

export interface AppIconEditorProps {
  app: Pick<AppAdminEntry, 'id' | 'name' | 'icon_url' | 'soft_deleted_at'>;
}

/** Clave i18n del error del servidor según el status (413/415/422). */
function serverErrorKey(error: ApiError | null, fallback: string): string {
  switch (error?.status) {
    case 413:
      return 'admin.icon.errors.tooLargeServer';
    case 415:
      return 'admin.icon.errors.badType';
    case 422:
      return 'admin.icon.errors.badDimensions';
    default:
      return fallback;
  }
}

/**
 * Vista previa del icono de una app y acciones "Cambiar icono" / "Quitar icono".
 * Valida tamaño y tipo en cliente antes de subir; el backend valida los bytes.
 */
export function AppIconEditor({ app }: AppIconEditorProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const errorId = useId();
  const upload = useUploadAppIcon();
  const remove = useDeleteAppIcon();
  const [clientError, setClientError] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);

  // Si cambia la URL (icono nuevo), se vuelve a intentar pintar la imagen.
  useEffect(() => setBroken(false), [app.icon_url]);

  const disabled = app.soft_deleted_at !== null || upload.isPending || remove.isPending;

  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // permite volver a elegir el mismo fichero
    if (!file) return;
    upload.reset();
    remove.reset();
    const problem = checkIconFile(file);
    if (problem) {
      setClientError(
        t(`admin.icon.errors.${problem}`, { size: Math.ceil(file.size / 1024) }),
      );
      return;
    }
    setClientError(null);
    upload.mutate({ id: app.id, file });
  };

  const onRemove = () => {
    if (!confirm(t('admin.icon.confirmRemove', { name: app.name }))) return;
    setClientError(null);
    upload.reset();
    remove.mutate(app.id);
  };

  let error = clientError;
  if (!error && upload.isError) {
    error = t(serverErrorKey(upload.error, 'admin.icon.errors.uploadFailed'));
  }
  if (!error && remove.isError) error = t('admin.icon.errors.removeFailed');

  return (
    <div className="flex items-center gap-2">
      {app.icon_url && !broken ? (
        <img
          src={app.icon_url}
          alt={t('admin.icon.previewAlt', { name: app.name })}
          onError={() => setBroken(true)}
          className="h-8 w-8 flex-none rounded border bg-white object-contain"
        />
      ) : (
        <span
          role="img"
          aria-label={t('admin.icon.none')}
          title={t('admin.icon.none')}
          className="flex h-8 w-8 flex-none items-center justify-center rounded border border-dashed bg-slate-50 text-xs font-semibold text-slate-400"
        >
          {app.name.charAt(0).toUpperCase()}
        </span>
      )}
      <div className="flex flex-col items-start gap-0.5">
        <div className="flex gap-2">
          <label
            htmlFor={inputId}
            title={t('admin.icon.hint')}
            className={`cursor-pointer text-xs text-appsol-900 hover:underline ${
              disabled ? 'pointer-events-none opacity-50' : ''
            }`}
          >
            {upload.isPending ? t('admin.icon.uploading') : t('admin.icon.change')}
          </label>
          <input
            id={inputId}
            type="file"
            accept={ICON_ACCEPT}
            className="sr-only"
            aria-label={t('admin.icon.changeFor', { name: app.name })}
            aria-describedby={error ? errorId : undefined}
            aria-invalid={error ? true : undefined}
            disabled={disabled}
            onChange={onFile}
          />
          {app.icon_url && (
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              aria-label={t('admin.icon.removeFor', { name: app.name })}
              className="text-xs text-rose-700 hover:underline disabled:opacity-50"
            >
              {t('admin.icon.remove')}
            </button>
          )}
        </div>
        {error && (
          <p id={errorId} role="alert" className="text-xs text-rose-700">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
